import Vue from 'vue';
import Vuex from 'vuex';
import { getQueueLength, setQueueLength, getSlots, getPhysicalQueueLength, finalizeSlot } from './APIService';
import { format } from 'date-fns';
import { last } from 'lodash';

Vue.use(Vuex);

const state = {
  queueTime: 0,
  physicalQueueTime: 0,
  phone: '',
  groupSize: 0,
  queueSlots: [],
  slot: {
    time: '-',
    number: '-',
  },
};

const roundToFiveMin = milliseconds => {
  const coeff = 1000 * 60 * 5;
  const rounded = Math.round(milliseconds / coeff) * coeff;
  return rounded / (60 * 1000);
};

// mutations are operations that actually mutates the state.
// each mutation handler gets the entire state tree as the
// first argument, followed by additional payload arguments.
// mutations must be synchronous and can be recorded by plugins
// for debugging purposes.
const mutations = {
  setQueueTime(state, newTime) {
    state.queueTime = newTime;
  },
  setPhysicalQueueTime(state, newTime) {
    state.physicalQueueTime = newTime;
  },
  setSlot(state, { id, time }) {
    state.slot.time = time;
    state.slot.number = id;
  },
  setDetails(state, { phone, groupSize }) {
    state.phone = phone;
    state.groupSize = groupSize;
  },
  setQueueSlots(state, queueSlots = []) {
    state.queueSlots = queueSlots.sort((a, b) => a.loadTime - b.loadTime);
  },
};

async function refreshQueueTime({ commit }) {
  const response = await getQueueLength();
  const queueTime = response;
  commit('setQueueTime', queueTime);
}

async function refreshPhysicalQueueTime({ commit }) {
  const response = await getPhysicalQueueLength();
  const queueTime = response;
  commit('setPhysicalQueueTime', queueTime);
}

async function refreshQueueSlots({ commit }) {
  const response = await getSlots();
  commit('setQueueSlots', response);
}

// actions are functions that cause side effects and can involve
// asynchronous operations.
const actions = {
  setQueueTime: async ({ commit }, newTime) => {
    await setQueueLength(newTime);
    commit('setQueueTime', newTime);
    refreshQueueTime({ commit });
    refreshPhysicalQueueTime({ commit });
  },
  refreshQueueTime,
  refreshPhysicalQueueTime,
  refreshQueueSlots,
  startRefreshScheduler: async ({ commit }) => {
    refreshQueueTime({ commit });
    refreshPhysicalQueueTime({ commit });
    refreshQueueSlots({ commit });
    setInterval(() => {
      refreshQueueTime({ commit });
      refreshPhysicalQueueTime({ commit });
      refreshQueueSlots({ commit });
    }, 10 * 1000);
  },
  setSlot: ({ commit }, slotOptions) => commit('setSlot', slotOptions),
  setDetails: ({ commit }, details) => commit('setDetails', details),
  finalizeSlot: async ({ commit }, slotId) => {
    const allSlots = await finalizeSlot({ slotId });
    commit('setQueueSlots', allSlots);
  },
};

// getters are functions
const getters = {
  queueTime: state => (state.queueTime === 0 ? '-' : roundToFiveMin(state.queueTime)),
  phone: state => state.phone,
  groupSize: state => state.groupSize,
  slotTime: state => format(state.slot.time, 'H:mm'),
  slotNumber: state => state.slot.number,
  queueSlots: state => state.queueSlots,
  physicalQueueTime: state => state.physicalQueueTime / (60 * 1000),
  currentSlot: state => {
    const unLoadedSlots = state.queueSlots.filter(slot => slot.loaded);
    const lastUsedSlot = last(unLoadedSlots);
    if (lastUsedSlot !== undefined) {
      return lastUsedSlot;
    } else {
      return null;
    }
  },
};

export default new Vuex.Store({
  state,
  getters,
  actions,
  mutations,
});
